import * as React from "react";
import { styled, SxProps, Box } from "@mui/material";
import {
  GetFrameRate,
  GetMarkers,
  GetRealCurrentFrame,
  GetTimeInSec,
  GetTotalFrameCount,
  GoToFrame,
  PlayPause,
  SetMarkByCurrentFrame,
  SetMarkInByFrame,
  SetMarkOutByFrame,
  SetPlaybackSpeed,
  SetVolume,
} from "../../../Redux/Reducers/redux-slice-react-player";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import {
  GetActiveTimeline,
  GetTimelineEventBeingEdited,
  TimelineType,
} from "../../../Redux/Reducers/redux-slice-timeline";
import { PlayerProgress, ProgressBarType } from "../ReactPlayerWrapper/player-progress";
import { GetCurrentFrame } from "../../../Redux/Reducers/redux-slice-timetool";
import { PreviewContext } from "../../../Pages/admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";

export type PlayerTimelinePlaceholder = {
  hasFirstPlayed: boolean;
  sx?: SxProps;
  size?: "small" | string;
};

const PlayerTimelinePlaceholder: React.FC<PlayerTimelinePlaceholder> = (props) => {
  const dispatch = useAppDispatch();
  const $timeline = React.useRef<HTMLElement>(null);
  const baseBoundingRect = React.useRef({ left: 0, width: 0 });
  const markers = useAppSelector(GetMarkers);
  const currentFrame = useAppSelector(GetRealCurrentFrame);
  const totalFrameCount = useAppSelector(GetTotalFrameCount);
  const activeTimeline = useAppSelector(GetActiveTimeline);
  const [localFrameMaker, setLocalFrameMaker] = React.useState({
    start: markers.markIn.markFrame,
    end: markers.markOut.markFrame,
  });

  const timelineEventBeingEdited = useAppSelector(GetTimelineEventBeingEdited);

  const [tooltipVisibility, setTooltipVisibility] = React.useState({ left: false, right: false });

  React.useEffect(() => {
    setLocalFrameMaker({
      start: markers.markIn.markFrame,
      end: markers.markOut.markFrame,
    });
  }, [markers.markIn, markers.markOut]);

  // Init player redux state
  React.useEffect(() => {
    if (timelineEventBeingEdited) {
      dispatch(SetMarkInByFrame(timelineEventBeingEdited.timecodes.in.markFrame));
      dispatch(SetMarkOutByFrame(timelineEventBeingEdited.timecodes.out.markFrame));
    } else {
      dispatch(SetMarkByCurrentFrame("In"));
      dispatch(SetMarkOutByFrame(currentFrame + 10));
    }
    dispatch(SetPlaybackSpeed(1));

    if (!props.hasFirstPlayed) {
      dispatch(PlayPause());
    }
  }, [timelineEventBeingEdited]);

  const pos = React.useMemo(() => {
    const left = (localFrameMaker.start * 100) / totalFrameCount;
    const right = (localFrameMaker.end * 100) / totalFrameCount;

    return {
      left,
      right,
    };
  }, [localFrameMaker.start, localFrameMaker.end, totalFrameCount]);

  const progressBarPos = React.useMemo(() => {
    const left = pos.left < pos.right ? pos.left : pos.right;
    const right = pos.left < pos.right ? pos.right : pos.left;

    return {
      left,
      right,
    };
  }, [pos]);

  const setSizes = React.useCallback(() => {
    if (!$timeline.current) {
      return;
    }
    const _pos = $timeline.current.getBoundingClientRect();
    baseBoundingRect.current.left = _pos.left;
    baseBoundingRect.current.width = $timeline.current.clientWidth;
  }, [$timeline.current]);

  React.useEffect(() => {
    setSizes();
    window.addEventListener("resize", setSizes);
    return () => window.removeEventListener("resize", setSizes);
  }, [setSizes]);

  return (
    <Box position="relative">
      <PlayerProgress type={ProgressBarType.Marker} context={PreviewContext.DistribEnlarged} />
      <StyledRail ref={$timeline} size={props.size}>
        <StyledProgress
          size={props.size}
          left={progressBarPos.left}
          right={progressBarPos.right}
          activeTimeline={activeTimeline ?? TimelineType.Clips}
          onClick={(e) => dispatch(GoToFrame(localFrameMaker.start))}
        ></StyledProgress>
      </StyledRail>
    </Box>
  );
};

export default PlayerTimelinePlaceholder;

const StyledProgress = styled(Box, {
  shouldForwardProp: (prop) => prop !== "left" && prop !== "right" && prop !== "size" && prop !== "activeTimeline",
})<{
  left: number;
  right: number;
  activeTimeline: TimelineType;
  size?: PlayerTimelinePlaceholder["size"];
}>(({ theme, left, right, activeTimeline }) => ({
  position: "absolute",
  height: "100%",
  bottom: 0,
  left: `${left}%`,
  width: `${right - left}%`,
  background: activeTimeline === "clips" ? theme.palette.primary.main : theme.palette.info.main,
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: 0.5,
  cursor: "pointer",
}));

const StyledRail = styled(Box, { shouldForwardProp: (prop) => prop !== "size" })<{
  size?: PlayerTimelinePlaceholder["size"];
}>(({ theme, size }) => ({
  position: "absolute",
  pointerEvents: "none",
  bottom: 0,
  width: "100%",
  height: 16,
}));
